/**
 * Path conventions for co-located lightmap build-data beside scene files.
 */
import { BUILD_DATA_ZIP_EXT, PROJECT_PATH_PREFIX, SCENE_NAME_EXT } from '../const.js';
import { AssetPath } from '../storage/storageProvider.js';

/** Browser-safe `.` / `..` collapse for virtual `@project/...` paths (no Node `path`). */
function normalizePosixPath(path: string): string {
  const parts: string[] = [];
  for (const segment of path.split('/')) {
    if (segment === '' || segment === '.') {
      continue;
    }
    if (segment === '..') {
      parts.pop();
      continue;
    }
    parts.push(segment);
  }
  return parts.join('/');
}

export function normalizeSceneAssetPath(scenePath: string): string {
  const slashes = AssetPath.withForwardSlashes(scenePath);
  const prefix = PROJECT_PATH_PREFIX + '/';
  if (slashes.startsWith(prefix)) {
    return normalizePosixPath(slashes);
  }
  const relative = slashes.replace(/^\.\//, '').replace(/^\/+/, '');
  return normalizePosixPath(AssetPath.join(PROJECT_PATH_PREFIX, relative));
}

/** Basename without extension, e.g. `default` from `.../default.genesys-scene`. */
export function sceneStemFromSceneAssetPath(sceneAssetPath: string): string {
  const normalized = normalizeSceneAssetPath(sceneAssetPath);
  if (!normalized.endsWith(SCENE_NAME_EXT)) {
    throw new Error(`Expected ${SCENE_NAME_EXT} scene path, got: ${sceneAssetPath}`);
  }
  const sceneBasename = normalized.split('/').pop() ?? normalized;
  return sceneBasename.slice(0, -SCENE_NAME_EXT.length);
}

/** Runtime zip beside the scene, e.g. `@project/assets/default.build-data.zip`. */
export function buildDataZipPathForScene(sceneAssetPath: string): string {
  const stem = sceneStemFromSceneAssetPath(sceneAssetPath);
  const normalized = normalizeSceneAssetPath(sceneAssetPath);
  const sceneDir = normalized.slice(0, normalized.lastIndexOf('/'));
  return `${sceneDir}/${stem}${BUILD_DATA_ZIP_EXT}`;
}
